import { useRef, Fragment } from "react";
import Barcode from "react-barcode";
import { isSunmiDevice, printOnSunmi } from "../../../utils/sunmiPrinter";
import { toast } from "../../../components/toast";

interface TransactionItem {
  id: string;
  name: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  vat?: number;
  total: number;
}

interface TransactionDetailsModalProps {
  onClose: () => void;
  transaction: {
    id: string;
    time: string;
    date: string;
    amount: string;
    items: number;
    cashier: string;
    customer?: string;
    paymentMethod: string;
    paymentMethods?: Array<{ method: string; amount: number }>;
    subtotal: number;
    tax: number;
    discount: number;
    total: number;
    itemsList: TransactionItem[];
  };
}

export default function TransactionDetailsModal({
  onClose,
  transaction,
}: TransactionDetailsModalProps) {
  const receiptRef = useRef<HTMLDivElement>(null);
  const totalLineDiscount = transaction.itemsList.reduce(
    (sum, item) => sum + Number(item.discount || 0),
    0,
  );
  const totalLineVat = transaction.itemsList.reduce(
    (sum, item) => sum + Number(item.vat || 0),
    0,
  );
  const totalDiscount =
    transaction.discount > 0 ? transaction.discount : totalLineDiscount;
  const orderDiscountAmount = Math.max(0, totalDiscount - totalLineDiscount);
  const effectiveVat = transaction.tax > 0 ? transaction.tax : totalLineVat;

  const counterInfo = JSON.parse(
    localStorage.getItem("counterCheckedInInfo") || "{}",
  );
  const terminal = counterInfo?.counterName || "Terminal";
  const paymentMethods = transaction.paymentMethods ?? [
    { method: transaction.paymentMethod, amount: transaction.total },
  ];
  const totalPaid = paymentMethods.reduce((sum, p) => sum + p.amount, 0);

  const handlePrint = async () => {
    if (isSunmiDevice()) {
      if (!receiptRef.current) return;
      if (!window.SunmiPrinter?.isConnected()) {
        toast.error(
          "Printer not ready. Please check the printer and try again.",
        );
        return;
      }
      try {
        await printOnSunmi(receiptRef.current);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Print failed");
      }
      return;
    }

    const BLK = "color:#000";
    const change =
      totalPaid > transaction.total ? totalPaid - transaction.total : 0;

    const itemsHtml = transaction.itemsList
      .map(
        (item) => `
      <tr style="font-size:14px;font-weight:bold;${BLK}">
        <td style="padding:3px 2px 0 0;word-break:break-word;${BLK}">${item.name}</td>
        <td style="text-align:right;white-space:nowrap;padding:3px 0 0 2px;${BLK}" rowspan="2">${item.total.toFixed(2)}</td>
      </tr>
      <tr style="font-size:12px;${BLK}">
        <td style="padding:0 2px 3px 0;${BLK}">${item.quantity} × ${item.unitPrice.toFixed(2)}${Number(item.vat || 0) > 0 ? ` | Vat:${Number(item.vat || 0).toFixed(2)}` : ""}${Number(item.discount || 0) > 0 ? ` | Disc:${Number(item.discount || 0).toFixed(2)}` : ""}</td>
      </tr>`,
      )
      .join("");

    const paymentsHtml = paymentMethods
      .map(
        (p) =>
          `<tr><td style="font-weight:bold;padding:3px 0;${BLK}">${p.method}</td><td style="font-weight:bold;text-align:right;padding:3px 0;${BLK}">${p.amount.toFixed(2)}</td></tr>`,
      )
      .join("");

    const html =
      `<!DOCTYPE html><html><head>
      <title>Receipt - ${transaction.id}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;color:#000}
        body{background:#fff}
        @media print{body{margin:0}}
      </style>
    </head><body>
      <div style="font-family:'Courier New',Courier,monospace;font-size:15px;font-weight:bold;line-height:1.6;max-width:300px;${BLK}">
        <div style="text-align:center;margin-bottom:6px">
          <img src="${window.location.origin}/golfer_logo.jpg" alt="Golfer" style="width:64px;height:64px;object-fit:contain;display:block;margin:0 auto 2px"/>
          <div style="font-size:20px;font-weight:bold;${BLK}">Golfer</div>
        </div>
        <div style="border-bottom:1px dotted #000;margin:8px 0;padding:4px 0"></div>
        <div style="text-align:center;font-weight:bold;font-size:17px;margin:5px 0;${BLK}">Order Receipt</div>
        <div style="border-bottom:1px dotted #000;margin:8px 0;padding:4px 0"></div>
        <table style="width:100%;border-collapse:collapse;font-size:16px;${BLK}">
          <tr><td style="font-weight:bold;padding:2px 0;width:40%;${BLK}">Customer:</td><td style="font-weight:bold;padding:2px 0;${BLK}">${transaction.customer || "Walk In Customer"}</td></tr>
          <tr><td style="font-weight:bold;padding:2px 0;${BLK}">Order No:</td><td style="font-weight:bold;padding:2px 0;${BLK}">${transaction.id}</td></tr>
          <tr><td style="font-weight:bold;padding:2px 0;${BLK}">Date:</td><td style="font-weight:bold;padding:2px 0;${BLK}">${transaction.date}</td></tr>
          <tr><td style="font-weight:bold;padding:2px 0;${BLK}">Time:</td><td style="font-weight:bold;padding:2px 0;${BLK}">${transaction.time}</td></tr>
          <tr><td style="font-weight:bold;padding:2px 0;${BLK}">User:</td><td style="font-weight:bold;padding:2px 0;${BLK}">${transaction.cashier}</td></tr>
          <tr><td style="font-weight:bold;padding:2px 0;${BLK}">Terminal:</td><td style="font-weight:bold;padding:2px 0;${BLK}">${terminal}</td></tr>
        </table>
        <div style="text-align:center;font-weight:bold;font-size:16px;margin:10px 0 5px;${BLK}">Sales Item</div>
        <table style="width:100%;border-collapse:collapse;font-size:14px;${BLK}">
          <thead>
            <tr style="border-top:1px dotted #000;border-bottom:1px dotted #000">
              <th style="text-align:left;padding:4px 2px 4px 0;font-weight:bold;${BLK}">Product</th>
              <th style="text-align:right;white-space:nowrap;padding:4px 0 4px 2px;font-weight:bold;${BLK}">Total</th>
            </tr>
          </thead>
          <tbody>${itemsHtml}</tbody>
        </table>
        <div style="border-bottom:1px dotted #000;margin:8px 0;padding:4px 0"></div>
        <table style="width:100%;border-collapse:collapse;font-size:16px;${BLK}">
          <tr><td style="font-weight:bold;padding:2px 0;${BLK}">Subtotal</td><td style="font-weight:bold;text-align:right;padding:2px 0;${BLK}">${transaction.subtotal.toFixed(2)}</td></tr>
          <tr><td style="font-weight:bold;padding:2px 0;${BLK}">Vat(+)</td><td style="font-weight:bold;text-align:right;padding:2px 0;${BLK}">${effectiveVat.toFixed(2)}</td></tr>
          ${totalDiscount > 0 ? `<tr><td style="font-weight:bold;padding:2px 0;${BLK}">You have saved</td><td style="font-weight:bold;text-align:right;padding:2px 0;${BLK}">${totalDiscount.toFixed(2)}</td></tr>` : ""}
        </table>
        <div style="border-top:1px dotted #000;border-bottom:2px solid #000;margin:5px 0 16px;padding:4px 0">
          <table style="width:100%;border-collapse:collapse;font-size:16px;${BLK}">
            <tr><td style="font-weight:bold;${BLK}">Total</td><td style="text-align:right;font-weight:bold;${BLK}">${transaction.total.toFixed(2)}</td></tr>
          </table>
        </div>
        <div style="text-align:center;font-weight:bold;font-size:15px;margin:10px 0 3px;${BLK}">Payments</div>
        <div style="border-bottom:1px dotted #000;margin:5px 0;padding:4px 0"></div>
        <table style="width:100%;border-collapse:collapse;font-size:16px;${BLK}">
          ${paymentsHtml}
          <tr><td style="font-weight:bold;padding:3px 0;${BLK}">Total Payment</td><td style="font-weight:bold;text-align:right;padding:3px 0;${BLK}">${totalPaid.toFixed(2)}</td></tr>
          ${change > 0 ? `<tr><td style="font-weight:bold;padding:3px 0;${BLK}">Change</td><td style="font-weight:bold;text-align:right;padding:3px 0;${BLK}">${change.toFixed(2)}</td></tr>` : ""}
        </table>
        <div style="text-align:center;margin:18px 0 5px">
          <canvas id="barcode-receipt"></canvas>
          <div style="font-size:14px;margin-top:3px;${BLK}">${transaction.id}</div>
        </div>
        <div style="text-align:center;margin-top:14px;border-top:1px dotted #000;padding-top:10px;${BLK}">
          <div style="margin-bottom:5px;font-size:15px;${BLK}">Thank you for shopping</div>
          <div style="font-weight:bold;font-size:15px;${BLK}">Customer Copy</div>
        </div>
        <div style="text-align:right;font-size:12px;${BLK};margin-top:10px">Powered by biznify.net</div>
      </div>
      <script src="https://unpkg.com/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></` +
      `script>
      <script>
        window.onload = function() {
          if(typeof JsBarcode !== 'undefined') {
            try { JsBarcode('#barcode-receipt', '${transaction.id}', {format:'CODE128',width:2,height:60,displayValue:false,margin:0}); } catch(e){}
          }
          setTimeout(function(){ window.print(); window.close(); }, 400);
        };
      </` +
      `script>
    </body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] min-h-[300px] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <i className="ri-file-list-3-line text-xl text-gray-700"></i>
            <h2 className="text-lg font-semibold text-gray-900">
              Transaction Details
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-lg p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-teal-700 mb-1">Transaction ID</p>
                <p className="text-2xl font-bold text-teal-900">
                  {transaction.id}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-teal-700 mb-1">Total Amount</p>
                <p className="text-3xl font-bold text-teal-900">
                  {transaction.amount}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-teal-700 mb-1">Date &amp; Time</p>
                <p className="text-sm font-semibold text-teal-900">
                  {transaction.date} at {transaction.time}
                </p>
              </div>
              <div>
                <p className="text-xs text-teal-700 mb-1">Payment Method</p>
                <p className="text-sm font-semibold text-teal-900">
                  {transaction.paymentMethod}
                </p>
              </div>
              <div>
                <p className="text-xs text-teal-700 mb-1">Cashier</p>
                <p className="text-sm font-semibold text-teal-900">
                  {transaction.cashier}
                </p>
              </div>
              <div>
                <p className="text-xs text-teal-700 mb-1">Customer</p>
                <p className="text-sm font-semibold text-teal-900">
                  {transaction.customer || "Walk-in"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-bold text-gray-900">
                Items ({transaction.items})
              </h3>
            </div>

            <div className="divide-y divide-gray-200">
              {transaction.itemsList.map((item) => (
                <div
                  key={item.id}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900 mb-1">
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-sm font-bold text-gray-900">
                        {item.total.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <div className="flex items-center gap-4">
                      <span>Qty: {item.quantity}</span>
                      <span>@ {item.unitPrice.toFixed(2)}</span>
                      <span className="text-red-600 font-medium">
                        Disc: {Number(item.discount || 0).toFixed(2)}
                      </span>
                      <span className="text-teal-700 font-medium">
                        VAT: {Number(item.vat || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-bold text-gray-900 mb-3">
              Payment Summary
            </h3>

            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Subtotal:</span>
                <span className="text-sm font-medium text-gray-900">
                  {transaction.subtotal.toFixed(2)}
                </span>
              </div>

              {totalDiscount > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-red-600">Discount:</span>
                  <span className="text-sm font-medium text-red-600">
                    -{totalDiscount.toFixed(2)}
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">VAT Total:</span>
                <span className="text-sm font-medium text-gray-900">
                  {effectiveVat.toFixed(2)}
                </span>
              </div>

              {paymentMethods.map((p, idx) => (
                <div
                  key={idx}
                  className="flex justify-between items-center py-2 border-b border-gray-100"
                >
                  <span className="text-sm text-gray-600">{p.method}:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {p.amount.toFixed(2)}
                  </span>
                </div>
              ))}

              <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300">
                <span className="text-base font-bold text-gray-900">
                  Total:
                </span>
                <span className="text-xl font-bold text-teal-600">
                  {transaction.total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t bg-gray-50 flex gap-3">
          <button
            onClick={handlePrint}
            className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            <i className="ri-printer-line text-base"></i>
            Print Receipt
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>

      {/* Hidden receipt for Sunmi thermal printing */}
      <div
        ref={receiptRef}
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: "576px",
          fontFamily: "Courier New, monospace",
          fontSize: "14px",
          fontWeight: "bold",
          lineHeight: "1.5",
          backgroundColor: "#ffffff",
          padding: "10px",
          color: "#000",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "4px" }}>
          <img
            src="/golfer_logo.jpg"
            alt="Golfer"
            style={{
              width: "64px",
              height: "64px",
              objectFit: "contain",
              display: "block",
              margin: "0 auto 1px",
            }}
          />
          <div style={{ fontSize: "16px", fontWeight: "bold" }}>Golfer</div>
        </div>
        <div style={{ borderBottom: "1px dotted #000", margin: "6px 0" }}></div>
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "13px",
            margin: "4px 0",
          }}
        >
          Order Receipt
        </div>
        <div style={{ borderBottom: "1px dotted #000", margin: "6px 0" }}></div>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "11px",
          }}
        >
          <tbody>
            {[
              ["Customer:", transaction.customer || "Walk In Customer"],
              ["Order No:", transaction.id],
              ["Date:", transaction.date],
              ["Time:", transaction.time],
              ["User:", transaction.cashier],
              ["Terminal:", terminal],
            ].map(([label, value]) => (
              <tr key={label}>
                <td
                  style={{ fontWeight: "bold", width: "40%", padding: "1px 0" }}
                >
                  {label}
                </td>
                <td style={{ padding: "1px 0" }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "16px",
            margin: "10px 0 5px",
            color: "#000",
          }}
        >
          Sales Item
        </div>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
            color: "#000",
          }}
        >
          <thead>
            <tr
              style={{
                borderTop: "1px dotted #000",
                borderBottom: "1px dotted #000",
              }}
            >
              <th
                style={{
                  textAlign: "left",
                  padding: "4px 2px 4px 0",
                  fontWeight: "bold",
                  color: "#000",
                }}
              >
                Product
              </th>
              <th
                style={{
                  textAlign: "right",
                  padding: "4px 0 4px 2px",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  color: "#000",
                }}
              >
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {transaction.itemsList.map((item, idx) => (
              <Fragment key={idx}>
                <tr>
                  <td
                    style={{
                      padding: "3px 2px 0 0",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      fontWeight: "bold",
                      color: "#000",
                    }}
                  >
                    {item.name}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      padding: "3px 0 0 2px",
                      whiteSpace: "nowrap",
                      fontWeight: "bold",
                      color: "#000",
                    }}
                    rowSpan={2}
                  >
                    {item.total.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "0 2px 3px 0",
                      fontSize: "11px",
                      color: "#000",
                    }}
                  >
                    {item.quantity} × {item.unitPrice.toFixed(2)}
                    {Number(item.vat || 0) > 0
                      ? ` | Vat:${Number(item.vat || 0).toFixed(2)}`
                      : ""}
                    {Number(item.discount || 0) > 0
                      ? ` | Disc:${Number(item.discount || 0).toFixed(2)}`
                      : ""}
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
        <div style={{ borderBottom: "1px dotted #000", margin: "6px 0" }}></div>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "11px",
          }}
        >
          <tbody>
            <tr>
              <td style={{ fontWeight: "bold", padding: "1px 0" }}>Subtotal</td>
              <td style={{ textAlign: "right" }}>
                {transaction.subtotal.toFixed(2)}
              </td>
            </tr>
            <tr>
              <td style={{ fontWeight: "bold", padding: "1px 0" }}>Vat(+)</td>
              <td style={{ textAlign: "right" }}>{effectiveVat.toFixed(2)}</td>
            </tr>
            {totalDiscount > 0 && (
              <tr>
                <td style={{ fontWeight: "bold", padding: "1px 0" }}>
                  You have saved
                </td>
                <td style={{ textAlign: "right" }}>
                  {totalDiscount.toFixed(2)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div
          style={{
            borderTop: "1px dotted #000",
            borderBottom: "2px dotted #000",
            margin: "4px 0",
            padding: "3px 0",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "12px",
            }}
          >
            <tbody>
              <tr>
                <td style={{ fontWeight: "bold" }}>Total</td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {transaction.total.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "12px",
            margin: "8px 0 2px",
          }}
        >
          Payments
        </div>
        <div style={{ borderBottom: "1px dotted #000", margin: "4px 0" }}></div>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "11px",
          }}
        >
          <tbody>
            {paymentMethods.map((p, idx) => (
              <tr key={idx}>
                <td style={{ fontWeight: "bold", padding: "2px 0" }}>
                  {p.method}
                </td>
                <td style={{ textAlign: "right", padding: "2px 0" }}>
                  {p.amount.toFixed(2)}
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ fontWeight: "bold", padding: "2px 0" }}>
                Total Payment
              </td>
              <td style={{ textAlign: "right", padding: "2px 0" }}>
                {totalPaid.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
        <div
          style={{
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            margin: "16px 0 4px",
          }}
        >
          <Barcode
            value={transaction.id || "0000000000"}
            format="CODE128"
            width={1.2}
            height={50}
            displayValue={false}
            margin={0}
          />
          <div style={{ color: "#000", fontSize: "11px", marginTop: "2px" }}>
            {transaction.id}
          </div>
        </div>
        <div
          style={{
            textAlign: "center",
            marginTop: "12px",
            borderTop: "1px dotted #000",
            paddingTop: "8px",
            fontSize: "10px",
          }}
        >
          <div style={{ marginBottom: "4px" }}>Thank you for shopping</div>
          <div style={{ fontWeight: "bold" }}>Customer Copy</div>
        </div>
        <div
          style={{
            textAlign: "right",
            fontSize: "9px",
            color: "#000",
            marginTop: "8px",
          }}
        >
          Powered by biznify.net
        </div>
      </div>
    </div>
  );
}
